import React, {useState} from 'react';
import {
    HiCheckCircle, HiChevronDown, HiChevronUp, HiExclamationCircle, HiSortAscending, HiSortDescending, HiXCircle
} from "react-icons/hi";
import ClipLoader from "react-spinners/ClipLoader";
import {format} from 'date-fns';
import {ArrowTopRightOnSquareIcon} from '@heroicons/react/24/outline';


const WiderVorlageTableComponent = ({data, loading = true, portal}) => {
    const [activeTab, setActiveTab] = useState('Alle');
    const [sortKey, setSortKey] = useState('');
    const [sortAsc, setSortAsc] = useState(true);

    const handleTabClick = (tab) => {
        setActiveTab(tab);
    };

    const handleSort = (key) => {
        setSortAsc(sortKey === key ? !sortAsc : true);
        setSortKey(key);
    };

    const filteredData = data.filter((item) => {
        if (activeTab === 'Alle') return true;
        if (activeTab === 'Störfälle') return item.Typ.toLowerCase() === 'störfall';
        if (activeTab === 'Wiedervorlagen') return item.Typ.toLowerCase() !== 'störfall';
        return true;
    });

    const sortedData = [...filteredData].sort((a, b) => {
        if (!sortKey) return 0;
        const valA = a[sortKey];
        const valB = b[sortKey];
        if (valA < valB) return sortAsc ? -1 : 1;
        if (valA > valB) return sortAsc ? 1 : -1;
        return 0;
    });

    const getRowColor = (item) => {
        const currentDate = new Date();
        const itemDate = new Date(item.Datum);

        if (item.Typ.toLowerCase() === "störfall") {
            return "bg-cancel";
        }
        if (itemDate > currentDate) {
            return "bg-complete";
        }
        if (itemDate < currentDate) {
            return "bg-pending";
        }
        return "";
    };

    const getStatusIcon = (item) => {
        const currentDate = new Date();
        const itemDate = new Date(item.Datum);

        if (item.Typ.toLowerCase() === "störfall") {
            return <HiXCircle size={20} className="text-red font-bold"/>;
        }
        if (itemDate > currentDate) {
            return <HiCheckCircle size={20} className="text-green font-bold"/>;
        }
        if (itemDate < currentDate) {
            return <HiExclamationCircle size={20} style={{color: "#FFA500"}}/>;
        }
        return null;
    };

    return (loading ? (<div className="flex items-center justify-center h-64">
        <ClipLoader size={50} color={'#b4b4b4'}/>
    </div>) : (<div>
        <div className="flex space-x-4 mb-2 border-b border-b-mainBlue">
            {['Alle', 'Störfälle', 'Wiedervorlagen'].map(tab => (<button
                key={tab}
                onClick={() => handleTabClick(tab)}
                className={`px-6 text-xl font-semibold py-2 ${activeTab === tab ? 'border-b-2 border-b-mainBlue text-blue-600' : 'text-gray-500'} hover:text-blue-600`}
            >
                {tab}
            </button>))}
        </div>
        <div className="overflow-y-auto w-full relative" style={{maxHeight: '35vh'}}>
            <table className="w-full bg-white rounded"
                   style={{fontSize: "14px", tableLayout: 'fixed', borderCollapse: "separate", borderSpacing: '0 6px'}}>
                <thead className="sticky top-0 z-10 bg-white">
                <tr className="bg-gray-100">
                    {['', 'WV-Datum', 'Firma Kurz', 'Ersteller', 'Wiedervorlage'].map((header, index) => {
                        // Define column widths based on the index
                        const widths = ['5%', '15%', '15%', '15%', '50%'];

                        // Map header names to the corresponding data keys
                        const keyMap = {
                            '': 'status',
                            'Datum': 'Datum',
                            'Firma Kurz': 'FirmaKurz',
                            'Ersteller': 'Ersteller',
                            'Wiedervorlage': 'Bemerkung'
                        };
                        const dataKey = keyMap[header];

                        return (<th
                            key={header}
                            onClick={() => handleSort(dataKey)}
                            style={{width: widths[index]}}
                            className="px-1 py-2 text-center text-gray-700 font-semibold cursor-pointer select-none"
                        >
                            <div className="flex items-center justify-start gap-1">
                                <span style={{lineHeight: "1.25", fontSize: "0.9rem"}}>{header}</span>
                                {index !== 0 && (<div className="flex flex-col items-center leading-none">
                                    <HiChevronUp
                                        className={`h-3 w-3 ${sortKey === dataKey && sortAsc ? "text-black" : "text-whiteDark"}`}
                                    />
                                    <HiChevronDown
                                        className={`h-3 w-3 ${sortKey === dataKey && !sortAsc ? "text-black" : "text-whiteDark"}`}
                                    />
                                </div>)}
                            </div>
                        </th>);
                    })}
                </tr>
                </thead>
                <tbody>
                {sortedData.map((item, index) => (<tr key={index}
                                                      className={`${getRowColor(item)} rounded bg-opacity-30 my-2`}>
                    <td className="px-1 py-2 text-center">{getStatusIcon(item)}</td>
                    <td className="p-1 text-left">{item.Datum ? format(new Date(item.Datum), 'dd.MM.yyyy') : ''}</td>
                    <td className="p-1 text-left" style={{
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"
                    }}><a
                        href={`/firmenprojekte/${portal}/${item.FP_ID}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-mainBlue flex-shrink-0 hover:underline"
                        title="Details anzeigen"
                    >{item.FirmaKurz}</a></td>
                    <td className="p-1 text-left" style={{
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"
                    }}>{item.Ersteller}</td>
                    <td className="p-1 text-left" style={{
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textAlign: "left"
                    }}>
                        <div className="flex items-center justify-between">
                            <span className="truncate">{item.Bemerkung}</span>
                            <a
                                href={`/firmenprojekte/${portal}/${item.FP_ID}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-mainBlue flex-shrink-0 ml-2"
                                title="Details anzeigen"
                            >
                                <ArrowTopRightOnSquareIcon className="h-5 w-5"/>
                            </a>
                        </div>
                    </td>
                </tr>))}
                </tbody>
            </table>
        </div>
    </div>));
};

export default WiderVorlageTableComponent;
